import React from 'react'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react'
import { format } from 'date-fns'
import type { OperatorReport } from '@/types'

interface OperatorReportDetailsProps {
  operatorReports: OperatorReport[]
}

const operatorMap: Record<string, { name: string; color: string; icon: string }> = {
  CMCC: { name: '中国移动', color: '#00A0E9', icon: '📱' },
  CUCC: { name: '中国联通', color: '#E60012', icon: '📞' },
  CTCC: { name: '中国电信', color: '#009639', icon: '📡' },
}

const statusMap: Record<string, { label: string; variant: 'secondary' | 'warning' | 'success' | 'destructive'; icon: any; color: string }> = {
  NOT_REPORTED: { label: '未报备', variant: 'secondary', icon: AlertCircle, color: 'text-gray-600' },
  REPORTING: { label: '报备中', variant: 'warning', icon: Clock, color: 'text-yellow-600' },
  REPORTED: { label: '已报备', variant: 'success', icon: CheckCircle, color: 'text-green-600' },
  REJECTED: { label: '报备被拒', variant: 'destructive', icon: XCircle, color: 'text-red-600' },
}

export function OperatorReportDetails({ operatorReports }: OperatorReportDetailsProps) {
  if (!operatorReports || operatorReports.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">运营商报备详情</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">暂无报备详情</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">运营商报备详情</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {operatorReports.map((report) => {
          const operator = operatorMap[report.operatorCode || '']
          const status = statusMap[report.reportStatus || '']
          const Icon = status.icon
          
          return (
            <div key={report.id} className="flex items-center justify-between p-3 border rounded-lg">
                              <div className="flex items-center space-x-3">
                  <div className="text-lg">{operator?.icon || '📡'}</div>
                  <div>
                    <div className="font-medium text-sm">{operator?.name || report.operatorName || '未知运营商'}</div>
                    <div className="text-xs text-muted-foreground">
                      {report.reportTime ? `报备时间: ${format(new Date(report.reportTime), 'yyyy-MM-dd HH:mm')}` : '未报备'}
                    </div>
                  </div>
                </div>
              
              <div className="flex items-center space-x-2">
                <Badge variant={status?.variant || 'secondary'} className="text-xs flex items-center gap-1">
                  <Icon className="h-3 w-3" />
                  {status?.label || '未知状态'}
                </Badge>
                {report.reportStatus === 'REPORTING' && (
                  <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                )}
              </div>
            </div>
          )
        })}
      </CardContent>
    </Card>
  )
}
